import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2, CheckCircle2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { SERVICES } from "../lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMES = [
  "07:00", "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  vehicle: "",
  service: "",
  preferred_date: null,
  preferred_time: "",
  notes: "",
};

export const Booking = ({ formRef }) => {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.phone.trim()) return "Please enter your phone number.";
    if (!form.vehicle.trim()) return "Please enter your vehicle make & model.";
    if (!form.service) return "Please select a service.";
    if (!form.preferred_date) return "Please pick a preferred date.";
    if (!form.preferred_time) return "Please pick a preferred time.";
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        preferred_date: format(form.preferred_date, "yyyy-MM-dd"),
      };
      await axios.post(`${API}/bookings`, payload);
      setSuccess(true);
      toast.success("Booking received! We'll be in touch shortly.");
      setForm(initial);
    } catch (e2) {
      console.error(e2);
      toast.error("Something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      ref={formRef}
      data-testid="booking-section"
      className="relative bg-[#050505] py-24 md:py-32 noise-overlay"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="red-bar" />
              <span className="font-mono-accent text-xs text-zinc-400">05 — BOOK NOW</span>
            </div>
            <h2 className="font-display font-bold uppercase text-white text-5xl md:text-7xl leading-[0.9] tracking-tighter mb-8">
              Lock in <br />your <span className="chrome-text">slot</span>.
            </h2>
            <p className="text-zinc-400 leading-relaxed mb-8 max-w-md">
              Tell us about your car and when suits you. We'll confirm by phone within 2 hours during business hours.
            </p>
            <div className="space-y-4 text-sm">
              <div className="flex items-center gap-4">
                <span className="font-mono-accent text-zinc-500 text-xs w-20">PHONE</span>
                <a href="tel:0412383817" className="text-white hover:text-[#DC2626]">0412 383 817</a>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono-accent text-zinc-500 text-xs w-20">HOURS</span>
                <span className="text-zinc-300">Mon–Sat · 7am — 6pm</span>
              </div>
              <div className="flex items-center gap-4">
                <span className="font-mono-accent text-zinc-500 text-xs w-20">AREA</span>
                <span className="text-zinc-300">Sydney & Surrounds</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[#0F0F0F] border border-[#27272A] p-8 md:p-12">
              {success ? (
                <div data-testid="booking-success" className="text-center py-16">
                  <CheckCircle2 className="h-16 w-16 text-[#DC2626] mx-auto mb-6" />
                  <h3 className="font-display font-bold uppercase text-white text-3xl tracking-tighter mb-3">
                    Booking received
                  </h3>
                  <p className="text-zinc-400 mb-8">We've got your request. Expect a call from us shortly to confirm your slot.</p>
                  <button
                    data-testid="booking-new-btn"
                    onClick={() => setSuccess(false)}
                    className="bg-[#DC2626] hover:bg-[#EF4444] text-white font-display font-bold uppercase tracking-wider text-sm px-8 py-4"
                  >
                    Make another booking
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-8" data-testid="booking-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <Field label="Full Name">
                      <input
                        data-testid="booking-name"
                        type="text"
                        value={form.name}
                        onChange={(e) => set("name", e.target.value)}
                        className={inputCls}
                        placeholder="Your name"
                      />
                    </Field>
                    <Field label="Email">
                      <input
                        data-testid="booking-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        className={inputCls}
                        placeholder="you@example.com"
                      />
                    </Field>
                    <Field label="Phone">
                      <input
                        data-testid="booking-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        className={inputCls}
                        placeholder="0400 000 000"
                      />
                    </Field>
                    <Field label="Vehicle (Make / Model / Year)">
                      <input
                        data-testid="booking-vehicle"
                        type="text"
                        value={form.vehicle}
                        onChange={(e) => set("vehicle", e.target.value)}
                        className={inputCls}
                        placeholder="e.g. BMW M3 2022"
                      />
                    </Field>
                    <Field label="Service">
                      <Select value={form.service} onValueChange={(v) => set("service", v)}>
                        <SelectTrigger
                          data-testid="booking-service-trigger"
                          className="bg-transparent border-0 border-b border-[#3F3F46] focus:border-[#DC2626] rounded-none text-white px-0 h-auto py-3 focus:ring-0 focus:outline-none"
                        >
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#27272A] text-white rounded-none">
                          {SERVICES.map((s) => (
                            <SelectItem key={s.id} value={s.title} data-testid={`booking-service-option-${s.id}`}>
                              {s.title}
                            </SelectItem>
                          ))}
                          <SelectItem value="Bronze Package">Bronze Package</SelectItem>
                          <SelectItem value="Silver Package">Silver Package</SelectItem>
                          <SelectItem value="Gold Package">Gold Package</SelectItem>
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Preferred Date">
                      <Popover>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            data-testid="booking-date-trigger"
                            className={`${inputCls} text-left flex items-center justify-between`}
                          >
                            <span className={form.preferred_date ? "text-white" : "text-zinc-500"}>
                              {form.preferred_date ? format(form.preferred_date, "EEE, MMM d, yyyy") : "Pick a date"}
                            </span>
                            <CalendarIcon className="h-4 w-4 text-zinc-400" />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          className="w-auto p-0 bg-[#121212] border-[#27272A] text-white rounded-none"
                          align="start"
                        >
                          <Calendar
                            mode="single"
                            selected={form.preferred_date}
                            onSelect={(d) => set("preferred_date", d)}
                            disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </Field>
                    <Field label="Preferred Time">
                      <Select value={form.preferred_time} onValueChange={(v) => set("preferred_time", v)}>
                        <SelectTrigger
                          data-testid="booking-time-trigger"
                          className="bg-transparent border-0 border-b border-[#3F3F46] focus:border-[#DC2626] rounded-none text-white px-0 h-auto py-3 focus:ring-0 focus:outline-none"
                        >
                          <SelectValue placeholder="Pick a time" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#121212] border-[#27272A] text-white rounded-none max-h-72">
                          {TIMES.map((t) => (
                            <SelectItem key={t} value={t}>{t}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                  <Field label="Notes (optional)">
                    <textarea
                      data-testid="booking-notes"
                      value={form.notes}
                      onChange={(e) => set("notes", e.target.value)}
                      rows={3}
                      className={`${inputCls} resize-none`}
                      placeholder="Anything we should know? e.g. extra-dirty interior, pickup address, etc."
                    />
                  </Field>
                  <button
                    type="submit"
                    data-testid="booking-submit"
                    disabled={submitting}
                    className="w-full bg-[#DC2626] hover:bg-[#EF4444] disabled:opacity-60 text-white font-display font-bold uppercase tracking-wider text-sm py-5 transition-colors flex items-center justify-center gap-3"
                  >
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                    {submitting ? "Sending..." : "Request Booking"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-[#27272A] focus:border-[#DC2626] text-white py-3 px-0 placeholder-zinc-600 focus:outline-none focus:ring-0 transition-colors";

const Field = ({ label, children }) => (
  <div>
    <label className="font-mono-accent text-[10px] text-zinc-500 mb-2 block">{label}</label>
    {children}
  </div>
);
